import type { WidgetData, WidgetMetrics } from './types';

/**
 * Typed error carrying the numeric HTTP status of a non-ok `fetchWidgetData`
 * response, so callers can classify the failure via `.status` instead of
 * parsing it out of the message string (spec FR-4.1). Lives here — not in
 * `packages/utils`, which AR-1.1 restricts to pure classifier/message
 * functions only. Network/abort failures still throw whatever `fetch` itself
 * throws, with no `.status` field.
 */
export class WidgetApiError extends Error {
  status: number;

  constructor(status: number) {
    super(`Widget API error: ${status}`);
    this.name = 'WidgetApiError';
    this.status = status;
  }
}

const WIDGET_DATA_PATH = '/widget/other';

const resolveApiBase = (): string => {
  const env = import.meta.env.VITE_API_ENDPOINT as string | undefined;
  if (!env || env.startsWith('PLACEHOLDER_')) return `https://api.plu.mx${WIDGET_DATA_PATH}`;
  return `${env.replace(/\/+$/, '')}${WIDGET_DATA_PATH}`;
};

const API_BASE = resolveApiBase();

/** In-memory request cache keyed by request URL */
const cache = new Map<string, Promise<WidgetData>>();

/** Track failed requests to avoid immediate retries */
const failedRequests = new Map<string, { timestamp: number; error: Error }>();
const RETRY_DELAY_MS = 5000; // Wait 5 seconds before retrying a failed request

interface FetchParams {
  type: string;
  identifierType: string;
  identifier: string;
  site: string;
  signal: AbortSignal;
}

export const fetchWidgetData = (params: FetchParams): Promise<WidgetData> => {
  const cacheKey = `${params.type}:${params.identifierType}:${params.identifier}:${params.site}`;

  // Check if this request failed recently and short-circuit retries for a short period.
  const failed = failedRequests.get(cacheKey);
  if (failed) {
    if (Date.now() - failed.timestamp < RETRY_DELAY_MS) {
      return Promise.reject(failed.error);
    }
    failedRequests.delete(cacheKey);
  }

  const cached = cache.get(cacheKey);
  if (cached) return cached;

  // URL: https://api.plu.mx/widget/other/\{type\}\?type\=\{identifierType\}\&id\=\{identifier\}\&site\=\{site\}
  const url = new URL(`${API_BASE}/${params.type}`);
  url.searchParams.set('type', params.identifierType);
  url.searchParams.set('id', params.identifier);
  url.searchParams.set('site', params.site);

  const promise = fetch(url.toString(), { signal: params.signal })
    .then(res => {
      if (!res.ok) throw new WidgetApiError(res.status);
      return res.json() as Promise<WidgetData>;
    })
    .catch(err => {
      const error = err instanceof Error ? err : new Error(String(err));
      failedRequests.set(cacheKey, { timestamp: Date.now(), error });
      cache.delete(cacheKey);
      throw error;
    });

  cache.set(cacheKey, promise);
  return promise;
};

/** Extracts flat WidgetMetrics — uses category_totals for efficiency */
export const extractMetrics = (data: WidgetData): WidgetMetrics => ({
  citation: data.category_totals?.citation?.total ?? 0,
  usage: data.category_totals?.usage?.total ?? 0,
  capture: data.category_totals?.capture?.total ?? 0,
  mention: data.category_totals?.mention?.total ?? 0,
  socialMedia: data.category_totals?.social_media?.total ?? 0,
});

export const hasAnyMetrics = (metrics: WidgetMetrics): boolean =>
  Object.values(metrics).some(v => v > 0);
